const COORD_TOLERANCE = 0.5;

function isRfiTag(tag) {
  if (!tag) return false;
  // Legacy RFI tags may omit type; exclude punch list / submittal explicitly.
  return tag.type !== "punchlist" && tag.type !== "submittal";
}

export function extractRfiDrawingLinks(annotations, drawingId) {
  if (!drawingId || !Array.isArray(annotations)) {
    return [];
  }

  const links = [];
  for (const ann of annotations) {
    if (ann?.type !== "connection") continue;
    const tag = ann.rfiTag;
    if (!isRfiTag(tag)) continue;

    const pos = tag.tagPosition;
    if (!tag.rfiId || pos?.x == null || pos?.y == null) {
      continue;
    }

    links.push({
      rfiId: String(tag.rfiId),
      drawingId: String(drawingId),
      x: pos.x,
      y: pos.y,
    });
  }
  return links;
}

function coordsMatch(a, b) {
  return Math.abs(Number(a) - Number(b)) <= COORD_TOLERANCE;
}

function linksMatch(a, b) {
  return (
    a.rfiId === b.rfiId &&
    a.drawingId === b.drawingId &&
    coordsMatch(a.x, b.x) &&
    coordsMatch(a.y, b.y)
  );
}

export function findRemovedRfiDrawingLinks(
  previousAnnotations,
  newAnnotations,
  drawingId,
) {
  const previousLinks = extractRfiDrawingLinks(previousAnnotations, drawingId);
  const newLinks = extractRfiDrawingLinks(newAnnotations, drawingId);

  return previousLinks.filter(
    (prev) => !newLinks.some((current) => linksMatch(prev, current)),
  );
}

export function findAddedRfiDrawingLinks(
  previousAnnotations,
  newAnnotations,
  drawingId,
) {
  const previousLinks = extractRfiDrawingLinks(previousAnnotations, drawingId);
  const newLinks = extractRfiDrawingLinks(newAnnotations, drawingId);

  return newLinks.filter(
    (current) => !previousLinks.some((prev) => linksMatch(prev, current)),
  );
}

export async function syncRfiDrawingLinksOnSave(
  previousAnnotations,
  newAnnotations,
  drawingId,
  { linkFn, unlinkFn },
) {
  const removed = findRemovedRfiDrawingLinks(
    previousAnnotations,
    newAnnotations,
    drawingId,
  );
  const added = findAddedRfiDrawingLinks(
    previousAnnotations,
    newAnnotations,
    drawingId,
  );

  if (!removed.length && !added.length) {
    return;
  }

  const unlinkResults = await Promise.allSettled(
    removed.map((link) =>
      unlinkFn(link.rfiId, link.drawingId, link.x, link.y),
    ),
  );
  const linkResults = await Promise.allSettled(
    added.map((link) => linkFn(link.rfiId, link.drawingId, link.x, link.y)),
  );

  const failed = [...unlinkResults, ...linkResults].filter(
    (r) => r.status === "rejected",
  );
  if (failed.length) {
    throw new Error(`Failed to sync ${failed.length} RFI drawing link(s).`);
  }
}
